import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import { masterFilesAPI } from "../services/api";

const MasterFiles = () => {
  // Add missing state variables
  const [selectedFiles, setSelectedFiles] = useState({
    3110: null,
    3119: null,
  });
  const [uploadProgress, setUploadProgress] = useState(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [masterData, setMasterData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(50);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadExistingMasterData();
    loadUploadedFiles();
  }, []);

  const loadExistingMasterData = async (page = 1, search = searchTerm) => {
    setPaginationLoading(true);
    try {
      console.log(`🔄 Loading master data page ${page} from database...`);

      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: search || undefined,
      };

      // Get data count first if it's the first page
      if (page === 1) {
        try {
          const countResponse = await masterFilesAPI.getDataCount();
          console.log("📊 Data count response:", countResponse.data);
          if (countResponse.data.success) {
            setTotalRecords(countResponse.data.count || 0);
            setTotalPages(
              Math.ceil((countResponse.data.count || 0) / pageSize)
            );
          }
        } catch (countError) {
          console.log("📊 Could not get data count:", countError.message);
        }
      }

      // Load master data with pagination
      const response = await masterFilesAPI.getMasterData(params);
      console.log("📊 Master data response:", response.data);

      if (response.data.success) {
        const data = response.data.data || [];
        console.log(
          `📊 Loaded ${data.length} master data records from database (page ${page})`
        );
        setMasterData(data);
        setCurrentPage(page);

        // Update total records if returned in response
        if (response.data.totalCount !== undefined) {
          setTotalRecords(response.data.totalCount);
          setTotalPages(Math.ceil(response.data.totalCount / pageSize));
        }

        if (data.length === 0 && response.data.message) {
          console.log("ℹ️ No data message:", response.data.message);
        }
      } else {
        console.warn("❌ Failed to load master data:", response.data.message);
        setMasterData([]);
      }
    } catch (error) {
      console.error("❌ Failed to load master data:", error);

      // Show user-friendly error message
      if (error.response?.status === 401) {
        alert("กรุณาเข้าสู่ระบบใหม่");
      } else if (error.response?.status === 500) {
        alert("เกิดข้อผิดพลาดกับเซิร์ฟเวอร์ กรุณาลองใหม่ภายหลัง");
      } else {
        alert("ไม่สามารถโหลดข้อมูล Master ได้ กรุณาตรวจสอบการเชื่อมต่อ");
      }
      setMasterData([]);
    } finally {
      setPaginationLoading(false);
      setLoading(false);
    }
  };

  const loadUploadedFiles = async () => {
    try {
      const response = await masterFilesAPI.getFiles();
      setUploadedFiles(response.data.data || []);
    } catch (error) {
      console.error("Failed to load uploaded files:", error);
      setUploadedFiles([]);
    }
  };

  const handleFileSelect = (fileType, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("กรุณาเลือกไฟล์ Excel (.xls หรือ .xlsx) เท่านั้น");
      return;
    }

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert("ขนาดไฟล์ต้องไม่เกิน 50 MB");
      return;
    }

    setSelectedFiles((prev) => ({
      ...prev,
      [fileType]: file,
    }));
  };

  const formatExcelDate = (value) => {
    if (!value) return "";

    try {
      // If it's already a string in YYYY-MM-DD format, return as is
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      // If it's a number (Excel serial date)
      if (typeof value === "number") {
        // Excel dates start from 1900-01-01 (with 1900 incorrectly treated as leap year)
        const excelEpoch = new Date(1900, 0, 1);
        const days = value - 1; // Excel is 1-based, JavaScript is 0-based
        const date = new Date(
          excelEpoch.getTime() + days * 24 * 60 * 60 * 1000
        );

        if (isNaN(date.getTime())) {
          return "";
        }

        return date.toISOString().split("T")[0];
      }

      // If it's already a Date object
      if (value instanceof Date) {
        if (isNaN(value.getTime())) {
          return "";
        }
        return value.toISOString().split("T")[0];
      }

      // Try to parse as string
      if (typeof value === "string") {
        // Handle various date formats
        const datePatterns = [
          /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/M/D or YYYY/MM/DD
          /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/D/YYYY or MM/DD/YYYY
          /^\d{4}-\d{1,2}-\d{1,2}$/, // YYYY-M-D or YYYY-MM-DD
          /^\d{1,2}-\d{1,2}-\d{4}$/, // M-D-YYYY or MM-DD-YYYY
        ];

        for (const pattern of datePatterns) {
          if (pattern.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return date.toISOString().split("T")[0];
            }
          }
        }
      }

      return "";
    } catch (error) {
      console.warn("Date parsing error:", error, "for value:", value);
      return "";
    }
  };

  const uploadMasterFiles = async () => {
    if (!selectedFiles["3110"] || !selectedFiles["3119"]) {
      alert("กรุณาเลือกไฟล์ครบทั้ง 2 ไฟล์ (3110 และ 3119)");
      return;
    }

    // Warning about data replacement
    if (
      !confirm(
        "⚠️ การอัปโหลดไฟล์ใหม่จะลบข้อมูล Master ทั้งหมดในฐานข้อมูลและแทนที่ด้วยข้อมูลใหม่\n\n" +
          "คุณแน่ใจหรือไม่ที่จะดำเนินการต่อ?\n\n" +
          "หมายเหตุ: การกระทำนี้ไม่สามารถยกเลิกได้"
      )
    ) {
      return;
    }

    // Check file sizes before upload
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const file3110Size = selectedFiles["3110"].size;
    const file3119Size = selectedFiles["3119"].size;

    if (file3110Size > maxFileSize) {
      alert(
        `ไฟล์ 3110 มีขนาดใหญ่เกินไป: ${(file3110Size / 1024 / 1024).toFixed(
          2
        )} MB (สูงสุด 50 MB)`
      );
      return;
    }

    if (file3119Size > maxFileSize) {
      alert(
        `ไฟล์ 3119 มีขนาดใหญ่เกินไป: ${(file3119Size / 1024 / 1024).toFixed(
          2
        )} MB (สูงสุด 50 MB)`
      );
      return;
    }

    const totalSize = file3110Size + file3119Size;
    console.log(
      `📊 Total upload size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`
    );

    setLoading(true);
    setUploadProgress(0);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file_3110", selectedFiles["3110"]);
      formData.append("file_3119", selectedFiles["3119"]);

      // Show upload progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 80) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10; // Slower progress to account for longer processing
        });
      }, 500); // Update every 500ms

      // Upload to backend database with data replacement
      const response = await masterFilesAPI.upload(formData);

      clearInterval(progressInterval);
      setUploadProgress(90);

      if (response.data.success) {
        // Get processed data from database
        const processedData = response.data.data;
        setMasterData(processedData.mergedData || []);
        setUploadedFiles(processedData.uploadedFiles || []);

        setUploadProgress(100);

        setTimeout(() => {
          setLoading(false);
          setUploadProgress(null);
          setActiveTab("data");
          // Reset files
          setSelectedFiles({
            3110: null,
            3119: null,
          });

          const replacedCount = processedData.replacedRecords || 0;
          const newCount = (processedData.mergedData || []).length;

          alert(
            `✅ ประมวลผล Master Files สำเร็จ!\n\n` +
              `🗑️ ลบข้อมูลเก่า: ${replacedCount.toLocaleString()} รายการ\n` +
              `📊 เพิ่มข้อมูลใหม่: ${newCount.toLocaleString()} รายการ\n\n` +
              `⏱️ เวลาประมวลผล: ${
                processedData.processingTime
                  ? Math.round(processedData.processingTime / 1000)
                  : "ไม่ทราบ"
              } วินาที`
          );
        }, 500);
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setLoading(false);
      setUploadProgress(null);

      // Show specific error messages
      if (error.message.includes("timeout")) {
        alert(
          "การอัปโหลดใช้เวลานานเกินไป\n\nแนะนำ:\n- ลองใช้ไฟล์ที่มีขนาดเล็กกว่า\n- ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต\n- ลองใหม่ในเวลาอื่น\n\n⚠️ ข้อมูลเก่าจะไม่ถูกลบหากการอัปโหลดล้มเหลว"
        );
      } else if (error.response?.status === 413) {
        alert("ไฟล์มีขนาดใหญ่เกินไป กรุณาลดขนาดไฟล์ให้น้อยกว่า 50MB");
      } else if (error.response?.status === 408) {
        alert("การประมวลผลใช้เวลานานเกินไป กรุณาลองใช้ไฟล์ที่มีข้อมูลน้อยกว่า");
      } else if (error.response?.status === 400) {
        alert("รูปแบบไฟล์ไม่ถูกต้อง กรุณาตรวจสอบไฟล์ Excel");
      } else if (error.response?.status === 401) {
        alert("ไม่มีสิทธิ์ในการอัปโหลด กรุณาเข้าสู่ระบบใหม่");
      } else if (error.response?.status === 500) {
        alert("เกิดข้อผิดพลาดในการประมวลผลไฟล์ กรุณาตรวจสอบรูปแบบข้อมูลในไฟล์");
      } else {
        alert(
          "เกิดข้อผิดพลาดในการอัปโหลด: " +
            (error.response?.data?.message || error.message) +
            "\n\nกรุณาตรวจสอบการเชื่อมต่อเซิร์ฟเวอร์\n\n⚠️ ข้อมูลเก่าจะไม่ถูกลบหากการอัปโหลดล้มเหลว"
        );
      }
    }
  };

  // Format display data - Only six master columns
  const formatDisplayData = (data) => {
    console.log("🔄 Formatting display data:", data);
    return data.map((item) => ({
      "Outlet Code": item.outlet_code || "",
      "Outlet Name": item.outlet_name || "",
      Site: item.site || "",
      "Site Name": item.site_name || "",
      "Outlet Type Description": item.outlet_type_description || "",
      "Outlet SubType Description": item.outlet_subtype_description || "",
    }));
  };

  const exportMasterData = async () => {
    try {
      if (masterData.length === 0) {
        alert("ไม่มีข้อมูลสำหรับส่งออก กรุณาอัปโหลดไฟล์ Master Files ก่อน");
        return;
      }
      // Check if XLSX is available
      if (typeof window.XLSX === "undefined") {
        alert("ไม่สามารถโหลด XLSX library ได้ กรุณารีเฟรชหน้าและลองใหม่");
        return;
      }
      // Only export the six master columns
      const exportData = masterData.map((item) => ({
        "Outlet Code": item.outlet_code || "",
        "Outlet Name": item.outlet_name || "",
        Site: item.site || "",
        "Site Name": item.site_name || "",
        "Outlet Type Description": item.outlet_type_description || "",
        "Outlet SubType Description": item.outlet_subtype_description || "",
      }));
      const ws = window.XLSX.utils.json_to_sheet(exportData);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, "Master Data");
      // Auto-size columns
      const max_width = exportData.reduce(
        (w, r) => Math.max(w, Object.keys(r).length),
        10
      );
      ws["!cols"] = Array.from({ length: max_width }, () => ({ wch: 15 }));
      window.XLSX.writeFile(
        wb,
        `master_data_database_${new Date().toISOString().split("T")[0]}.xlsx`
      );
    } catch (error) {
      console.error("Export failed:", error);
      alert("เกิดข้อผิดพลาดในการส่งออกข้อมูล: " + error.message);
    }
  };

  const deleteMasterFile = async (fileId) => {
    if (
      !confirm(
        "คุณแน่ใจหรือไม่ที่จะลบไฟล์นี้? ข้อมูลทั้งหมดจากไฟล์นี้จะถูกลบออกจากฐานข้อมูลอย่างถาวร"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await masterFilesAPI.deleteMasterData(fileId);

      // Reload data from database only
      await loadUploadedFiles();
      await loadExistingMasterData();

      alert("ลบไฟล์และข้อมูลจากฐานข้อมูลสำเร็จ");
    } catch (error) {
      console.error("Delete failed:", error);
      alert(
        "เกิดข้อผิดพลาดในการลบไฟล์: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle search with pagination reset
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
    loadExistingMasterData(1, searchValue);
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      loadExistingMasterData(page, searchTerm);
    }
  };

  // Refresh data
  const refreshData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadExistingMasterData(currentPage),
        loadUploadedFiles(),
      ]);
      alert("รีเฟรชข้อมูลสำเร็จ");
    } catch (error) {
      console.error("Refresh failed:", error);
      alert("เกิดข้อผิดพลาดในการรีเฟรชข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = formatDisplayData(masterData);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>จัดการ Master Files</h1>
          <p>
            อัปโหลดและจัดการไฟล์ 3110 (Type/Subtype) และ 3119 (Status/Date) -
            ข้อมูลจัดเก็บในฐานข้อมูลเท่านั้น
          </p>
        </div>
      </div>

      {/* Database Only Alert */}
      <div className="alert alert-success" style={{ marginBottom: "24px" }}>
        <span>✅</span>
        <div>
          <strong>ฐานข้อมูลเท่านั้น:</strong> ระบบจัดเก็บข้อมูล Master Files
          ในฐานข้อมูล MySQL เท่านั้น
          <br />
          <small>
            ไม่มีการใช้ localStorage - ข้อมูลปลอดภัยและเข้าถึงได้จากทุกอุปกรณ์
          </small>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === "upload" ? "active" : ""}`}
          onClick={() => setActiveTab("upload")}
        >
          อัปโหลดไฟล์
        </button>
        <button
          className={`tab ${activeTab === "files" ? "active" : ""}`}
          onClick={() => setActiveTab("files")}
        >
          ไฟล์ในฐานข้อมูล ({uploadedFiles.length})
        </button>
        <button
          className={`tab ${activeTab === "data" ? "active" : ""}`}
          onClick={() => setActiveTab("data")}
        >
          ข้อมูล Master ({filteredData.length.toLocaleString()})
        </button>
      </div>

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              อัปโหลด Master Files ลงฐานข้อมูล MySQL
            </h3>
          </div>

          <div className="grid grid-cols-2" style={{ marginBottom: "24px" }}>
            {/* 3110 File */}
            <div>
              <label className="form-label">ไฟล์ 3110 (Type/Subtype)</label>
              <div
                className="upload-area"
                style={{ padding: "20px", minHeight: "140px" }}
              >
                {selectedFiles["3110"] ? (
                  <div>
                    <div className="upload-icon">✅</div>
                    <div className="upload-text">
                      {selectedFiles["3110"].name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginTop: "8px",
                      }}
                    >
                      ข้อมูล: Site, Outlet, Type, Subtype
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="upload-icon">📄</div>
                    <div className="upload-text">3110.xlsx</div>
                    <div className="upload-subtext">
                      ข้อมูล Outlet Type และ Subtype
                    </div>
                    <input
                      type="file"
                      accept=".xls,.xlsx"
                      onChange={(e) =>
                        handleFileSelect("3110", e.target.files[0])
                      }
                      style={{ display: "none" }}
                      id="3110-upload"
                    />
                    <label
                      htmlFor="3110-upload"
                      className="btn btn-outline btn-sm"
                      style={{ marginTop: "8px" }}
                    >
                      เลือกไฟล์
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* 3119 File */}
            <div>
              <label className="form-label">ไฟล์ 3119 (Status/Date)</label>
              <div
                className="upload-area"
                style={{ padding: "20px", minHeight: "140px" }}
              >
                {selectedFiles["3119"] ? (
                  <div>
                    <div className="upload-icon">✅</div>
                    <div className="upload-text">
                      {selectedFiles["3119"].name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginTop: "8px",
                      }}
                    >
                      ข้อมูล: Status, Opening/Closing Date
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="upload-icon">📄</div>
                    <div className="upload-text">3119.xlsx</div>
                    <div className="upload-subtext">
                      ข้อมูล Status และ วันที่เปิด/ปิด
                    </div>
                    <input
                      type="file"
                      accept=".xls,.xlsx"
                      onChange={(e) =>
                        handleFileSelect("3119", e.target.files[0])
                      }
                      style={{ display: "none" }}
                      id="3119-upload"
                    />
                    <label
                      htmlFor="3119-upload"
                      className="btn btn-outline btn-sm"
                      style={{ marginTop: "8px" }}
                    >
                      เลือกไฟล์
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {uploadProgress !== null && (
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span>กำลังประมวลผล...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  background: "#e2e8f0",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: "100%",
                    background: "#6366f1",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <div
                style={{ fontSize: "12px", color: "#64748b", marginTop: "8px" }}
              >
                {uploadProgress < 40 && "กำลังอัปโหลดไฟล์..."}
                {uploadProgress >= 40 &&
                  uploadProgress < 80 &&
                  "กำลังประมวลผลไฟล์..."}
                {uploadProgress >= 80 &&
                  uploadProgress < 100 &&
                  "กำลังบันทึกข้อมูลลงฐานข้อมูล..."}
                {uploadProgress === 100 && "เสร็จสิ้น!"}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div style={{ textAlign: "center" }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={uploadMasterFiles}
              disabled={
                loading || !selectedFiles["3110"] || !selectedFiles["3119"]
              }
            >
              {loading ? "กำลังประมวลผล..." : "อัปโหลดและแทนที่ข้อมูลทั้งหมด"}
            </button>
          </div>

          {/* File Requirements */}
          <div className="alert alert-info" style={{ marginTop: "24px" }}>
            <span>ℹ️</span>
            <div>
              <strong>ข้อกำหนดไฟล์:</strong>
              <ul style={{ margin: "8px 0 0 20px", fontSize: "14px" }}>
                <li>
                  <strong>ไฟล์ 3110:</strong> ต้องมี columns: Site, Outlet,
                  Master Outlet Type, Master Outlet Subtype
                </li>
                <li>
                  <strong>ไฟล์ 3119:</strong> ต้องมี columns: Site, Outlet,
                  Master Outlet Status, Opening Date, Closing Date
                </li>
                <li>รองรับไฟล์ .xls และ .xlsx เท่านั้น</li>
                <li>
                  <strong>ขนาดไฟล์สูงสุด: 50 MB ต่อไฟล์</strong>
                </li>
                <li>
                  <strong>
                    ⚠️ การอัปโหลดจะลบข้อมูล Master
                    ทั้งหมดและแทนที่ด้วยข้อมูลใหม่
                  </strong>
                </li>
                <li>
                  <strong>
                    ไฟล์จะถูกประมวลผลและบันทึกลงฐานข้อมูล MySQL โดยตรง
                  </strong>
                </li>
                <li>
                  <strong>ไม่มีการจัดเก็บในเครื่องผู้ใช้ (localStorage)</strong>
                </li>
                <li>ข้อมูลจะรวมกันโดยใช้ Outlet Code เป็นหลัก</li>
                <li>
                  <strong>
                    หากไฟล์มีข้อมูลมาก อาจใช้เวลาในการประมวลผลนานขึ้น
                  </strong>
                </li>
                <li>
                  <strong style={{ color: "#f59e0b" }}>
                    🔄 ใช้ Transaction - หากอัปโหลดล้มเหลว ข้อมูลเก่าจะไม่ถูกลบ
                  </strong>
                </li>
              </ul>
            </div>
          </div>

          {/* Data Replacement Warning */}
          <div className="alert alert-warning" style={{ marginTop: "16px" }}>
            <span>⚠️</span>
            <div>
              <strong>คำเตือนสำคัญ:</strong>
              <p style={{ margin: "8px 0", fontSize: "14px" }}>
                การอัปโหลดไฟล์ Master Files ใหม่จะ
                <strong>ลบข้อมูลทั้งหมด</strong>ในฐานข้อมูลและ
                <strong>แทนที่ด้วยข้อมูลใหม่</strong>จากไฟล์ที่อัปโหลด
              </p>
              <p style={{ margin: "8px 0", fontSize: "14px" }}>
                • ข้อมูล Master เก่าทั้งหมดจะหายไป
                <br />
                • ระบบใช้ Database Transaction เพื่อป้องกันข้อมูลเสียหาย
                <br />• หากการอัปโหลดล้มเหลว ข้อมูลเก่าจะยังคงอยู่
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Files Tab */}
      {activeTab === "files" && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">ไฟล์ในฐานข้อมูล</h3>
            <p
              style={{
                color: "#64748b",
                fontSize: "14px",
                margin: "8px 0 0 0",
              }}
            >
              แสดงไฟล์ที่อัปโหลดและประมวลผลแล้วในฐานข้อมูล
            </p>
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ชื่อไฟล์</th>
                  <th>ประเภท</th>
                  <th>ขนาด</th>
                  <th>วันที่อัปโหลด</th>
                  <th>สถานะ</th>
                  <th>จำนวนข้อมูล</th>
                  <th>การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.length > 0 ? (
                  uploadedFiles.map((file) => (
                    <tr key={file.id}>
                      <td style={{ fontWeight: "600" }}>
                        {file.original_filename}
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {file.file_type}
                        </span>
                      </td>
                      <td>{(file.file_size / 1024).toFixed(2)} KB</td>
                      <td>
                        {new Date(file.upload_date).toLocaleDateString("th-TH")}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            file.processing_status === "COMPLETED"
                              ? "badge-success"
                              : file.processing_status === "FAILED"
                              ? "badge-error"
                              : "badge-warning"
                          }`}
                        >
                          {file.processing_status}
                        </span>
                      </td>
                      <td>{file.record_count?.toLocaleString() || 0}</td>
                      <td>
                        {hasPermission("canDeleteData") && (
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => deleteMasterFile(file.id)}
                            style={{ color: "#ef4444" }}
                          >
                            🗑️ ลบ
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      ยังไม่มีไฟล์ที่อัปโหลด
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Master Data Tab */}
      {activeTab === "data" && (
        <div>
          {/* Search and Export */}
          <div className="card" style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="ค้นหารหัสร้าน, ชื่อร้าน, หรือ Site..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
              {hasPermission("canExportData") && (
                <button className="btn btn-success" onClick={exportMasterData}>
                  📥 ส่งออก Excel
                </button>
              )}
              <button
                className="btn btn-outline"
                onClick={refreshData}
                disabled={loading}
              >
                🔄 {loading ? "กำลังรีเฟรช..." : "รีเฟรชจากฐานข้อมูล"}
              </button>
            </div>
          </div>

          {/* Master Data Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                ข้อมูล Master จากฐานข้อมูล MySQL (
                {totalRecords.toLocaleString()} รายการทั้งหมด)
              </h3>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  margin: "8px 0 0 0",
                }}
              >
                แสดงหน้า {currentPage} จาก {totalPages} หน้า ({pageSize}{" "}
                รายการต่อหน้า)
                {masterData.length > 0 && (
                  <span style={{ marginLeft: "10px", color: "#10b981" }}>
                    ✅ เชื่อมต่อฐานข้อมูลสำเร็จ
                  </span>
                )}
                {masterData.length === 0 && totalRecords === 0 && (
                  <span style={{ marginLeft: "10px", color: "#f59e0b" }}>
                    ⚠️ ไม่พบข้อมูลในฐานข้อมูล
                  </span>
                )}
              </p>
            </div>

            {/* Pagination Controls - Top */}
            {totalPages > 1 && (
              <div
                style={{
                  padding: "16px 24px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ color: "#64748b", fontSize: "14px" }}>
                    แสดงรายการ {(currentPage - 1) * pageSize + 1} -{" "}
                    {Math.min(currentPage * pageSize, totalRecords)} จากทั้งหมด{" "}
                    {totalRecords.toLocaleString()} รายการ
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || paginationLoading}
                    >
                      ⏮️ แรก
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || paginationLoading}
                    >
                      ◀️ ก่อนหน้า
                    </button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const startPage = Math.max(1, currentPage - 2);
                      const endPage = Math.min(totalPages, currentPage + 2);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            className={`btn ${
                              i === currentPage ? "btn-primary" : "btn-outline"
                            }`}
                            style={{ padding: "6px 12px", minWidth: "40px" }}
                            onClick={() => handlePageChange(i)}
                            disabled={paginationLoading}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}

                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || paginationLoading}
                    >
                      ▶️ ถัดไป
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || paginationLoading}
                    >
                      ⏭️ สุดท้าย
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Outlet Code</th>
                    <th>Outlet Name</th>
                    <th>Site</th>
                    <th>Site Name</th>
                    <th>Outlet Type Description</th>
                    <th>Outlet SubType Description</th>
                  </tr>
                </thead>
                <tbody>
                  {paginationLoading ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "10px",
                          }}
                        >
                          <div className="spinner"></div>
                          กำลังโหลดข้อมูลหน้า {currentPage}...
                        </div>
                      </td>
                    </tr>
                  ) : filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <tr key={index}>
                        <td>{item["Outlet Code"]}</td>
                        <td>{item["Outlet Name"]}</td>
                        <td>{item["Site"]}</td>
                        <td>{item["Site Name"]}</td>
                        <td>{item["Outlet Type Description"]}</td>
                        <td>{item["Outlet SubType Description"]}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {loading ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "10px",
                            }}
                          >
                            <div className="spinner"></div>
                            กำลังโหลดข้อมูลจากฐานข้อมูล MySQL...
                          </div>
                        ) : totalRecords === 0 ? (
                          <div>
                            <strong>
                              ไม่มีข้อมูล Master Files ในฐานข้อมูล
                            </strong>
                            <br />
                            <small>
                              กรุณาอัปโหลดไฟล์ 3110 และ 3119 เพื่อเริ่มต้นใช้งาน
                              <br />
                              ข้อมูลจะถูกบันทึกลงฐานข้อมูล MySQL โดยตรง
                            </small>
                            <div style={{ marginTop: "15px" }}>
                              <button
                                className="btn btn-primary"
                                onClick={() => setActiveTab("upload")}
                              >
                                อัปโหลดไฟล์ Master
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <strong>ไม่พบข้อมูลที่ตรงกับการค้นหา</strong>
                            <br />
                            <small>
                              ลองเปลี่ยนคำค้นหาหรือเคลียร์ตัวกรองเพื่อแสดงข้อมูลทั้งหมด
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Bottom */}
            {totalPages > 1 && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ color: "#64748b", fontSize: "14px" }}>
                    แสดงรายการ {(currentPage - 1) * pageSize + 1} -{" "}
                    {Math.min(currentPage * pageSize, totalRecords)} จากทั้งหมด{" "}
                    {totalRecords.toLocaleString()} รายการ
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || paginationLoading}
                    >
                      ⏮️ แรก
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || paginationLoading}
                    >
                      ◀️ ก่อนหน้า
                    </button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const startPage = Math.max(1, currentPage - 2);
                      const endPage = Math.min(totalPages, currentPage + 2);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            className={`btn ${
                              i === currentPage ? "btn-primary" : "btn-outline"
                            }`}
                            style={{ padding: "6px 12px", minWidth: "40px" }}
                            onClick={() => handlePageChange(i)}
                            disabled={paginationLoading}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}

                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || paginationLoading}
                    >
                      ▶️ ถัดไป
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: "6px 12px" }}
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || paginationLoading}
                    >
                      ⏭️ สุดท้าย
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterFiles;
